import React from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Login from './login';
import SubmitConfirmation from './submitConfirmation';
import Header from './header';
import Footer from './footer';
import Loader from '../components/loader';
import auth from '../service/authRepository';
import { setSubmitToken } from '../store/reports';

// Allowed values for the `report-action` query param. When present with one of
// these values (e.g. /?token=ey&report-action=print-all) the user lands on the
// submit confirmation page instead of the account-number login flow.
const ALLOWED_ACTIONS = ['print-all', 'inscribe-print-all'];

// Shown on the existing warning page when the token cannot be verified.
const INVALID_LINK_MESSAGE = 'This link is invalid or has expired.';

const RootLanding = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const action = searchParams.get('report-action');
  const token = searchParams.get('token');

  const isActionFlow = action && ALLOWED_ACTIONS.includes(action);

  // 'pending' while the token is being verified; 'valid' once the OneClick
  // service confirms it. Invalid tokens never reach 'valid' — the effect
  // redirects the user to the existing warning page instead.
  const [verifyState, setVerifyState] = React.useState('pending');

  React.useEffect(() => {
    if (!isActionFlow) return undefined;

    const redirectToWarning = () =>
      navigate('/status', {
        state: { status: 'WARNING', message: INVALID_LINK_MESSAGE },
      });

    if (!token) {
      redirectToWarning();
      return undefined;
    }

    let active = true;
    (async () => {
      try {
        const response = await auth.verifyToken({ token });
        if (!active) return;
        if (response?.data?.status === 'VALID') {
          // Persist the token so the submit step can send it; the service
          // derives uid/sid/reportInstruction from it.
          dispatch(setSubmitToken(token));
          setVerifyState('valid');
        } else {
          redirectToWarning();
        }
      } catch (error) {
        if (active) redirectToWarning();
      }
    })();

    return () => {
      active = false;
    };
  }, [isActionFlow, token, navigate, dispatch]);

  if (isActionFlow) {
    if (verifyState === 'valid') {
      return (
        <>
          <Header />
          <SubmitConfirmation />
          <Footer />
        </>
      );
    }
    // Verifying (or about to redirect): show the branded loader only. The
    // footer is held back until the confirmation page is ready, so it does not
    // flash beneath the loader.
    return (
      <>
        <Header />
        <Loader />
      </>
    );
  }

  // Existing account-number login flow, unchanged (Header only, no Footer).
  return (
    <>
      <Header />
      <Login />
    </>
  );
};

export default RootLanding;
